import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Award, Target, Users, HandshakeIcon, ShieldCheck, TrendingUp } from 'lucide-react';
import { Button } from '../components/ui/button';
import { HERO_IMAGES } from '../mock';

const VALUES = [
  { icon: ShieldCheck, title: 'Trust & Transparency', copy: 'Honest pricing, no hidden fees and clear communication at every stage of the shipment.' },
  { icon: Target, title: 'Operational Excellence', copy: 'Precision dispatch backed by real industry experience — drivers, brokers and logistics specialists.' },
  { icon: Users, title: 'Customer-First', copy: 'Every shipment is treated as if it were our own. Your satisfaction is our north star.' },
];

const About = () => {
  return (
    <>
      <Header />
      <main>
        {/* Hero */}
        <section className="relative overflow-hidden bg-[#0A1628]">
          <div className="absolute inset-0 z-0">
            <img src={HERO_IMAGES.ops} alt="Excelence Inc auto transport fleet on US highway" className="w-full h-full object-cover opacity-50" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0A1628]/95 via-[#0A1628]/85 to-[#0E2744]/60" />
          </div>
          <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 py-20 md:py-28 text-white">
            <span className="text-xs font-bold uppercase tracking-[0.25em] text-[#D4A24C]">About us</span>
            <h1 className="mt-4 text-4xl md:text-6xl font-extrabold leading-[1.05] max-w-3xl">
              Built by the people who move the industry.
            </h1>
            <p className="mt-5 max-w-2xl text-lg text-white/80">
              Excelence Inc was established in New York in 2024 — on a foundation of more than a decade of hands-on transportation experience.
            </p>
          </div>
        </section>

        {/* Story */}
        <section className="section bg-white">
          <div className="max-w-7xl mx-auto px-4 md:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-xs font-bold uppercase tracking-[0.25em] gold-text">Our story</span>
              <h2 className="mt-3 text-3xl md:text-4xl font-extrabold navy-text leading-tight">A decade of road experience, poured into one company.</h2>
              <div className="mt-6 space-y-4 text-[#4A5A72] leading-relaxed">
                <p>Long before opening our doors, our team spent years working as drivers, dispatchers and brokers. This hands-on background allows us to understand every stage of the transportation process with precision and insight.</p>
                <p>At Excelence Inc, we are committed to superior car transportation and freight coordination services throughout New York and beyond. Our mission is to streamline the logistics experience with clear communication, honest pricing, and a strong focus on customer satisfaction.</p>
                <p>With a foundation built on real industry experience and a dedication to continuous improvement, Excelence Inc stands as a trusted partner for clients seeking dependable transportation and logistics solutions.</p>
              </div>
              <div className="mt-8 flex gap-3">
                <a href="/#quote"><Button className="btn-accent font-bold">Get a Quote</Button></a>
                <a href="/contact"><Button variant="outline" className="font-semibold">Talk to us</Button></a>
              </div>
            </div>
            <div className="relative">
              <img src={HERO_IMAGES.main} alt="Team" className="rounded-2xl shadow-xl aspect-[4/5] object-cover" />
              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl p-5 border border-[#E5EAF2] max-w-xs">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-[#0A1628] flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-[#D4A24C]" />
                  </div>
                  <div>
                    <div className="text-2xl font-extrabold navy-text">10+</div>
                    <div className="text-xs text-[#4A5A72]">Years combined experience</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Values */}
        <section className="section bg-[#F7F9FC]">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="max-w-2xl mb-12">
              <span className="text-xs font-bold uppercase tracking-[0.25em] gold-text">What we value</span>
              <h2 className="mt-3 text-3xl md:text-4xl font-extrabold navy-text leading-tight">The principles that drive every shipment.</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {VALUES.map((v, i) => (
                <div key={i} className="card-lift bg-white rounded-2xl p-7 border border-[#E5EAF2]">
                  <div className="w-12 h-12 rounded-xl bg-[#0A1628] flex items-center justify-center mb-5">
                    <v.icon className="w-6 h-6 text-[#D4A24C]" />
                  </div>
                  <h3 className="text-xl font-extrabold navy-text mb-2">{v.title}</h3>
                  <p className="text-sm text-[#4A5A72]">{v.copy}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA strip */}
        <section className="bg-white py-14">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="bg-[#0A1628] rounded-3xl p-8 md:p-14 text-white flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div>
                <h3 className="text-2xl md:text-3xl font-extrabold">Plan it. Ship it. Trust it.</h3>
                <p className="text-white/70 mt-2">Ready to experience logistics done right?</p>
              </div>
              <a href="/#quote"><Button className="btn-accent font-bold h-12 px-8">Start your shipment</Button></a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
};

export default About;
